/**
 * CPOC-105: a staff member's own staff token, saved by /connect-poc for the chat on
 * www.tirerack.com to use instead of the site's anonymous token.
 *
 * Why storage works: the chat iframe on tirerack.com is this app on its own origin
 * (traci.mi.tirerack.com, or dev./qa. under it). That origin is the same SITE as
 * www.tirerack.com, so browsers do not partition its storage there; the iframe reads
 * what /connect-poc wrote on the same origin. No tirerack.com change, nothing in a URL.
 *
 * It grants nothing by itself: the server still routes to Connect only for someone
 * allowlisted, active and opted in. Anyone else holding a forged value gets a 401 and
 * the entry is dropped.
 */

export const STAFF_SITE_KEY = 'traci_staff_site_token';
/** Stop using it a minute early so no turn starts on a token about to die. */
const MARGIN_MS = 60 * 1000;
/** The only page whose chat takes it. */
export const STAFF_SITE_PARENT = 'https://www.tirerack.com';

export interface StaffSiteToken {
  token: string;
  email: string;
  expiresAt: number;
}

export function readStaffSiteToken(now: number = Date.now()): StaffSiteToken | null {
  try {
    const rec = JSON.parse(localStorage.getItem(STAFF_SITE_KEY) || 'null');
    if (
      rec && typeof rec.token === 'string' && rec.token.startsWith('ptr_') &&
      typeof rec.expiresAt === 'number' && rec.expiresAt - now > MARGIN_MS
    ) {
      return { token: rec.token, email: typeof rec.email === 'string' ? rec.email : '', expiresAt: rec.expiresAt };
    }
    if (rec) localStorage.removeItem(STAFF_SITE_KEY);
  } catch { /* storage blocked or garbage */ }
  return null;
}

export function saveStaffSiteToken(token: string, email: string, expiresInSeconds: number): StaffSiteToken {
  const rec = { token, email, expiresAt: Date.now() + expiresInSeconds * 1000 };
  localStorage.setItem(STAFF_SITE_KEY, JSON.stringify(rec));
  return rec;
}

export function clearStaffSiteToken(): void {
  try { localStorage.removeItem(STAFF_SITE_KEY); } catch { /* storage blocked */ }
}

/** Drop the saved token if it is the one that just failed. */
export function dropStaffSiteTokenIf(token: string | null | undefined): void {
  if (!token) return;
  try {
    const rec = JSON.parse(localStorage.getItem(STAFF_SITE_KEY) || 'null');
    if (rec && rec.token === token) localStorage.removeItem(STAFF_SITE_KEY);
  } catch { /* storage blocked */ }
}

/**
 * True only inside a frame whose parent page is www.tirerack.com. Uses
 * location.ancestorOrigins (Chrome, Safari, Edge); where that is missing (Firefox),
 * the referrer's origin, which a cross-origin frame always gets.
 */
export function inTirerackFrame(win: Window = window): boolean {
  try {
    if (win.parent === win) return false;
    const anc = win.location.ancestorOrigins;
    if (anc && anc.length > 0) return anc[0] === STAFF_SITE_PARENT;
    return !!win.document.referrer && new URL(win.document.referrer).origin === STAFF_SITE_PARENT;
  } catch {
    return false;
  }
}
